import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useImpersonation } from '@/contexts/ImpersonationContext';

export const UNREAD_COUNT_CHANGED_EVENT = 'chat-unread-count-changed';

/**
 * Hook that returns the total count of unread messages across all conversations.
 * Uses server-side SQL function + realtime subscription for new messages.
 * Refetches from server when a conversation is marked as read (via custom event).
 */
export const useTotalUnreadCount = () => {
  const { effectiveUserId } = useImpersonation();
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const myConversationIdsRef = useRef<Set<string>>(new Set());

  const fetchUnreadCount = useCallback(async () => {
    if (!effectiveUserId) {
      setTotalUnreadCount(0);
      return;
    }

    try {
      const { data: conversations, error: convError } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', effectiveUserId)
        .is('left_at', null);

      if (convError || !conversations?.length) {
        setTotalUnreadCount(0);
        return;
      }

      const conversationIds = conversations.map(c => c.conversation_id);
      myConversationIdsRef.current = new Set(conversationIds);

      const { data: unreadData, error: unreadError } = await supabase
        .rpc('get_unread_counts', {
          p_user_id: effectiveUserId,
          p_conversation_ids: conversationIds
        });

      if (unreadError) {
        console.error('[UnreadCount] Error:', unreadError);
        return;
      }

      const total = (unreadData as any[])?.reduce(
        (sum: number, row: any) => sum + Number(row.unread_count), 0
      ) || 0;

      setTotalUnreadCount(total);
    } catch (error) {
      console.error('[UnreadCount] Error:', error);
    }
  }, [effectiveUserId]);

  useEffect(() => {
    fetchUnreadCount().finally(() => setIsLoading(false));
  }, [fetchUnreadCount]);

  // Realtime: increment on new incoming messages
  useEffect(() => {
    if (!effectiveUserId) return;

    const channel = supabase
      .channel('unread-count-changes')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload) => {
          const newMsg = payload.new as any;
          if (newMsg.sender_id === effectiveUserId) return;
          if (!myConversationIdsRef.current.has(newMsg.conversation_id)) return;
          setTotalUnreadCount(prev => prev + 1);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [effectiveUserId]);

  // Refetch from server when a conversation is marked as read
  useEffect(() => {
    const handler = () => { fetchUnreadCount(); };
    window.addEventListener(UNREAD_COUNT_CHANGED_EVENT, handler);
    return () => window.removeEventListener(UNREAD_COUNT_CHANGED_EVENT, handler);
  }, [fetchUnreadCount]);

  return { totalUnreadCount, isLoading };
};
